import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as AccountApplicationsService from '../services/AccountApplicationsService';
import { AccountApplication } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const accountApplicationsKeys = {
    all: ['accountApplications'] as const,
    lists: () => [...accountApplicationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...accountApplicationsKeys.lists(), { filters }] as const,
    details: () => [...accountApplicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...accountApplicationsKeys.details(), id] as const,
};

// Hooks
export const useAccountApplicationsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: accountApplicationsKeys.list(filters),
        queryFn: () => AccountApplicationsService.fetchRecords(filters),
    });
};

export const useAccountApplicationQuery = (id: string) => {
    return useQuery({
        queryKey: accountApplicationsKeys.detail(id),
        queryFn: () => AccountApplicationsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountApplicationsService.add,
        onSuccess: () => {
            notifySuccess('Account application created');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: AccountApplication }) =>
            AccountApplicationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Account application updated');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountApplicationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Account application deleted');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useAccountApplicationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
